import { formatUrl, handleApiRequest } from "@cpj-ai/panel-npm-package";
import {
  AuthAccountContactTypeChoices,
  UpdateProfileMutation,
  UpdateProfileMutationVariables,
  useUpdateProfileMutation,
} from "@graphql/generated";
import { IOrganizationStoreState } from "@store/useOrganizationStore";
import { useCallback } from "react";
import toast from "react-hot-toast";
import { ICompanyInformationFormData } from "../formConfig";

export const useProfileSubmit = (
  selectedOrganization: (ICompanyInformationFormData & { id: string }) | null,
  setSelectedOrganization: (org: IOrganizationStoreState["selectedOrganization"]) => void,
) => {
  const [updateProfile, { loading }] = useUpdateProfileMutation();

  const handleSaveProfile = useCallback(
    async (data: ICompanyInformationFormData) => {
      if (!selectedOrganization?.id) {
        toast.error("Please select an organization");
        return;
      }

      const variables = {
        ...data,
        organizationId: selectedOrganization.id,
        city: typeof data.city === "string" ? data.city : data.city?.node?.id,
        businessType: typeof data.businessType === "string" ? data.businessType : data.businessType?.value,
        industry: data.industry?.node?.id,
        size: typeof data.size === "string" ? data.size : data.size?.value,
        type: typeof data.type === "string" ? data.type : data.type?.value,
        contacts: [
          {
            type: AuthAccountContactTypeChoices.WEBSITE,
            value: formatUrl(data.website ?? ""),
            organizationId: selectedOrganization.id,
          },
          {
            type: AuthAccountContactTypeChoices.PHONE,
            value: data.contactNumber ?? "",
            organizationId: selectedOrganization?.id,
          },
          {
            type: AuthAccountContactTypeChoices.ADDRESS,
            value: data.address ?? "",
            organizationId: selectedOrganization.id,
          },
        ].filter((item) => item.value && item.value !== ""),
      };

      await handleApiRequest<UpdateProfileMutation, UpdateProfileMutationVariables>({
        apiFunction: updateProfile,
        variables,
        componentName: "ProfileInformationForm",
        actionName: "updateProfile",
        onSuccess: (response) => {
          const updatedOrg = response.data?.account.organization?.update?.organization;
          if (updatedOrg) {
            setSelectedOrganization(updatedOrg);
          }
        },
        successMessage: "Profile updated successfully",
        fallbackErrorMessage: "Failed to update profile",
        extraErrorContext: {
          organizationId: selectedOrganization.id,
        },
      });
    },
    [selectedOrganization?.id, updateProfile, setSelectedOrganization],
  );

  return { handleSaveProfile, loading };
};
